import { getLowestLevelAssetIds } from 'components/choosers/ComponentChooser/helpers';
import {
  getTableColumnFilterValues,
  getTableSiteFilterIds,
} from 'components/data/helpers/tableFilterUtils';
import { type TQueryFilter } from 'components/DataTable/types';
import { AtlasGqlFilterOperator } from 'types/atlas-graphql';

// Work Orders already used `asset`; Tasks persist the Component column under `component`.
// Support both buckets so existing saved table state keeps working after the filter move.
const COMPONENT_FILTER_COLUMN_KEYS = ['asset', 'component'];

const getComponentFilterValues = (
  filters: Record<string, unknown> | undefined,
  filterKey: string
): string[] => {
  for (const columnKey of COMPONENT_FILTER_COLUMN_KEYS) {
    const values = getTableColumnFilterValues(filters, columnKey, filterKey);
    if (values.length) {
      return values;
    }
  }

  return [];
};

export const getWorkOrderTableAssetIds = (
  filters: Record<string, unknown> | undefined
): string[] | undefined => {
  const siteIds = getTableSiteFilterIds(filters);

  if (!siteIds.length) {
    return undefined;
  }

  const turbineIds = getTableColumnFilterValues(filters, 'turbine');
  const componentIds = turbineIds.length ? getComponentFilterValues(filters, 'asset') : [];

  return getLowestLevelAssetIds(
    siteIds,
    turbineIds.length ? turbineIds : undefined,
    componentIds.length ? componentIds : undefined
  );
};

export const getWorkOrderTableComponentTypes = (
  filters: Record<string, unknown> | undefined
): string[] | undefined => {
  const siteIds = getTableSiteFilterIds(filters);
  const turbineIds = siteIds.length ? getTableColumnFilterValues(filters, 'turbine') : [];

  if (!turbineIds.length) {
    return undefined;
  }

  const componentTypes = getComponentFilterValues(filters, 'componentType');
  return componentTypes.length ? componentTypes : undefined;
};

// Derived server inputs shared by the Work Order and Task tables, which both translate their
// Site/Turbine/Component column filters into `assetIds` and an `assetType` universal filter.
export const getWorkOrderTableAssetVariables = (
  filters: Record<string, unknown> | undefined
): { assetIds: string[] | undefined } => ({
  assetIds: getWorkOrderTableAssetIds(filters),
});

export const getWorkOrderTableComponentTypeFilter = (
  filters: Record<string, unknown> | undefined
): TQueryFilter[] => {
  const componentTypes = getWorkOrderTableComponentTypes(filters);

  return componentTypes
    ? [{ key: 'assetType', operator: AtlasGqlFilterOperator.Equals, values: componentTypes }]
    : [];
};
